import { useState, useEffect, useRef, useCallback } from "react";
import { BrowserRouter, Routes, Route, Link, useNavigate, useParams, Navigate } from "react-router-dom";
import axios from "axios";
import "./App.css";
import getAPIUrl from "./components/API_FIX";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import { TargetIcon } from "./components/icons";
import UserManagement from "./components/UserManagement";
import ShootersList from "./components/ShootersList";
import ShooterDetail from "./components/ShooterDetail";
import MatchReport from "./components/MatchReport";
import ScoreEntry from "./components/ScoreEntry";
import EditScore from "./components/EditScore";
import EditMatch from "./components/EditMatch";
import ChangePassword from "./components/ChangePassword";
import LeagueList from "./components/LeagueList";
import ScorecardLabels from "./components/ScorecardLabels";

// Get Backend URL from environment variable
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Build API base via helper (null-safe if the env var is missing at module load)
const API = getAPIUrl(BACKEND_URL || '');

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading, isAdmin } = useAuth();
  const navigate = useNavigate();
  
  useEffect(() => {
    // If not loading and not authenticated, redirect to login
    if (!loading && !user) {
      navigate('/login');
    }
    
    // If authenticated but not admin and route requires admin, redirect to unauthorized
    if (!loading && user && adminOnly && !isAdmin()) {
      navigate('/unauthorized');
    }
  }, [user, loading, adminOnly, isAdmin, navigate]);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <svg className="animate-spin h-10 w-10 text-brand-600 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-ink-500">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return null; // Will be redirected by the useEffect
  }
  
  if (adminOnly && !isAdmin()) {
    return null; // Will be redirected by the useEffect
  }
  
  return children;
};

// Unauthorized Page
const Unauthorized = () => {
  return (
    <div className="page max-w-lg text-center">
      <div className="card card-pad">
        <h1 className="page-title mb-3">Unauthorized Access</h1>
        <p className="mb-5 text-ink-600">You do not have permission to access this resource.</p>
        <Link to="/" className="btn-primary">
          Go to Home
        </Link>
      </div>
    </div>
  );
};

// Navbar Component
const Navbar = () => {
  const { user, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  
  const handleLogout = () => {
    // Call the logout function that was destructured from useAuth
    logout();
    // Navigate to login page
    navigate('/login');
  };

  return (
    <nav className="sticky top-0 z-30 bg-gradient-to-r from-ink-900 to-ink-800 text-white shadow-pop">
      <div className="container mx-auto flex flex-wrap items-center justify-between gap-3 px-4 py-3">
        <Link to="/" className="flex items-center gap-2.5 text-lg font-bold tracking-tight">
          <span className="grid h-9 w-9 place-items-center rounded-xl bg-white/10 ring-1 ring-white/20">
            <TargetIcon className="h-6 w-6 text-white" />
          </span>
          <span>Match Score Tracker</span>
        </Link>

        {user ? (
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex flex-wrap items-center gap-1">
              <Link to="/" className="nav-link">Home</Link>
              <Link to="/shooters" className="nav-link">Shooters</Link>
              <Link to="/leagues" className="nav-link">Leagues</Link>
              <Link to="/matches" className="nav-link">Matches</Link>
              <Link to="/labels" className="nav-link">Labels</Link>
              {isAdmin() && (
                <Link to="/admin/users" className="nav-link">Users</Link>
              )}
              <Link to="/change-password" className="nav-link">Change Password</Link>
            </div>

            <div className="flex items-center gap-2">
              <span className="badge bg-white/15 text-white ring-white/25">
                {isAdmin() ? 'Admin' : 'Reporter'}
              </span>
              <button
                onClick={handleLogout}
                className="btn-sm rounded-lg bg-white/10 font-semibold text-white ring-1 ring-inset ring-white/25 transition-colors hover:bg-white/20"
              >
                Logout
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-1">
            <Link to="/labels" className="nav-link">Labels</Link>
            <Link to="/login" className="nav-link">Login</Link>
            <Link to="/register" className="nav-link">Register</Link>
          </div>
        )}
      </div>
    </nav>
  );
};

// Login Page
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const { login, loginWithGoogle, user } = useAuth();
  const navigate = useNavigate();
  const googleBtnRef = useRef(null);

  // If user is already logged in, redirect to home
  useEffect(() => {
    if (user) {
      navigate('/');
    }
  }, [user, navigate]);

  // Google Sign-In: only when a client ID is configured at build time.
  const handleGoogle = useCallback(
    async (response) => {
      setError("");
      const ok = await loginWithGoogle(response.credential);
      if (ok) navigate('/');
      else setError("Google sign-in failed. Try again or use email/password.");
    },
    [loginWithGoogle, navigate]
  );

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return undefined;
    const render = () => {
      if (!window.google?.accounts?.id || !googleBtnRef.current) return;
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogle,
      });
      window.google.accounts.id.renderButton(googleBtnRef.current, {
        theme: "outline",
        size: "large",
        width: 320,
        text: "signin_with",
      });
    };
    if (window.google?.accounts?.id) {
      render();
      return undefined;
    }
    let script = document.getElementById("gis-script");
    if (!script) {
      script = document.createElement("script");
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.defer = true;
      script.id = "gis-script";
      document.body.appendChild(script);
    }
    script.addEventListener("load", render);
    return () => script.removeEventListener("load", render);
  }, [handleGoogle]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoggingIn(true);
    
    try {
      console.log("Attempting login with email:", email);
      const success = await login(email, password);
      
      if (success) {
        console.log("Login successful, navigating to home");
        navigate('/');
      } else {
        console.error("Login failed in component");
        
        // Check if token was set anyway
        const token = localStorage.getItem('token');
        if (token) {
          console.log("Token found after login, proceeding to home");
          navigate('/');
          return;
        }
        
        setError("Invalid email or password. Please verify your credentials and try again.");
      }
    } catch (err) {
      console.error("Login error in component:", err);
      setError(`Login error: ${err.message || "Server communication error"}`);
    } finally {
      setIsLoggingIn(false);
    }
  };

  return (
    <div className="page max-w-md">
      <div className="mb-6 flex flex-col items-center text-center">
        <span className="grid h-14 w-14 place-items-center rounded-2xl bg-brand-50 text-brand-600 ring-1 ring-brand-100">
          <TargetIcon className="h-8 w-8" />
        </span>
        <h1 className="page-title mt-3">Welcome back</h1>
        <p className="page-subtitle">Log in to manage matches, rosters, and scores.</p>
      </div>
      <div className="card card-pad">
        {error && <div className="alert-error mb-4">{error}</div>}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="field-label">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input"
              required
            />
          </div>

          <div>
            <label htmlFor="password" className="field-label">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input"
              required
            />
          </div>

          <button type="submit" disabled={isLoggingIn} className="btn-primary w-full">
            {isLoggingIn ? "Logging in..." : "Log in"}
          </button>
        </form>

        {GOOGLE_CLIENT_ID && (
          <>
            <div className="my-4 flex items-center gap-3 text-xs text-ink-400">
              <span className="h-px flex-1 bg-ink-200" />
              or
              <span className="h-px flex-1 bg-ink-200" />
            </div>
            <div ref={googleBtnRef} className="flex justify-center" />
          </>
        )}

        <div className="mt-6 text-center">
          <p className="text-ink-500">
            Don't have an account?{" "}
            <Link to="/register" className="font-semibold text-brand-600 hover:underline">
              Register here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

// Register Page
const Register = () => {
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [isRegistering, setIsRegistering] = useState(false);
  const { register, user } = useAuth();
  const navigate = useNavigate();

  // If user is already logged in, redirect to home
  useEffect(() => {
    if (user) {
      navigate('/');
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    
    setIsRegistering(true);
    
    try {
      console.log("Submitting registration for:", email);
      const success = await register(username, email, password);
      
      if (success) {
        console.log("Registration and auto-login successful");
        navigate('/');
      } else {
        console.error("Registration failed in component");
        // Check local storage to see if we have a token anyway
        const token = localStorage.getItem('token');
        if (token) {
          console.log("Token found after registration, proceeding to home");
          navigate('/');
          return;
        }
        
        setError("Registration failed. The email may already be in use or there was a server error.");
      }
    } catch (err) {
      console.error("Registration exception:", err);
      setError(`Registration error: ${err.message || "Unknown error"}`);
    } finally {
      setIsRegistering(false);
    }
  };

  return (
    <div className="page max-w-md">
      <div className="mb-6 flex flex-col items-center text-center">
        <span className="grid h-14 w-14 place-items-center rounded-2xl bg-brand-50 text-brand-600 ring-1 ring-brand-100">
          <TargetIcon className="h-8 w-8" />
        </span>
        <h1 className="page-title mt-3">Create an account</h1>
      </div>
      <div className="card card-pad">
        {error && <div className="alert-error mb-4">{error}</div>}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="username" className="field-label">
              Username
            </label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="input"
              required
            />
          </div>

          <div>
            <label htmlFor="register-email" className="field-label">
              Email
            </label>
            <input
              id="register-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input"
              required
            />
          </div>

          <div>
            <label htmlFor="register-password" className="field-label">
              Password
            </label>
            <input
              id="register-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input"
              required
            />
          </div>

          <div>
            <label htmlFor="confirm-password" className="field-label">
              Confirm Password
            </label>
            <input
              id="confirm-password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="input"
              required
            />
          </div>

          <button type="submit" disabled={isRegistering} className="btn-primary w-full">
            {isRegistering ? "Registering..." : "Register"}
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-ink-500">
            Already have an account?{" "}
            <Link to="/login" className="font-semibold text-brand-600 hover:underline">
              Log in here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

// Home Page
const Home = () => {
  return (
    <div className="page">
      <div className="relative mb-8 overflow-hidden rounded-2xl bg-gradient-to-br from-ink-900 to-ink-800 p-8 text-white shadow-pop sm:p-10">
        {/* Subtle bullseye rings motif */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-16 -top-16 h-72 w-72 rounded-full opacity-20"
          style={{
            background:
              "repeating-radial-gradient(circle, rgba(255,255,255,0.5) 0 2px, transparent 2px 26px)",
          }}
        />
        <div className="relative max-w-2xl">
          <span className="badge bg-white/15 text-white ring-white/25">Conventional Pistol</span>
          <h1 className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">Match Score Tracker</h1>
          <p className="mt-3 text-ink-200">
            Manage shooters, rosters, matches, and scores — then produce NRA-style
            Results Bulletins with place awards, special categories, and class boards.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/matches" className="btn bg-white font-semibold text-brand-700 hover:bg-brand-50">
              View Matches
            </Link>
            <Link to="/shooters" className="btn bg-white/10 font-semibold text-white ring-1 ring-inset ring-white/30 hover:bg-white/20">
              View Shooters
            </Link>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {[
          {
            title: "Shooters",
            body: "Manage shooter profiles, including NRA and CMP numbers. View shooter performance history.",
            to: "/shooters",
            cta: "View Shooters",
          },
          {
            title: "Matches",
            body: "Create and manage shooting matches. Define match structure, date, and location.",
            to: "/matches",
            cta: "View Matches",
          },
          {
            title: "Score Reports",
            body: "View match reports and shooter performance statistics across multiple matches.",
            to: "/matches",
            cta: "View Reports",
          },
        ].map((c) => (
          <div key={c.title} className="card card-pad transition-shadow hover:shadow-card-hover">
            <div className="mb-3 inline-grid h-10 w-10 place-items-center rounded-xl bg-brand-50 text-brand-600 ring-1 ring-brand-100">
              <TargetIcon className="h-6 w-6" />
            </div>
            <h2 className="section-title mb-2">{c.title}</h2>
            <p className="mb-4 text-sm text-ink-500">{c.body}</p>
            <Link to={c.to} className="btn-primary btn-sm">
              {c.cta}
            </Link>
          </div>
        ))}
      </div>
    </div>
  );
};

// Matches List with Year Filter
const MatchesList = () => {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newMatch, setNewMatch] = useState({ 
    name: "", 
    date: new Date().toISOString().split('T')[0],
    location: "",
    match_types: [],
    aggregate_type: "None",
    league_id: ""
  });
  const [showForm, setShowForm] = useState(false);
  const [formStep, setFormStep] = useState(1);
  const [matchTypes, setMatchTypes] = useState(null);
  const [instanceCounter, setInstanceCounter] = useState(1);
  const [selectedYear, setSelectedYear] = useState("all");
  const [availableYears, setAvailableYears] = useState([]);
  const [leagues, setLeagues] = useState([]);

  const { isAdmin } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch matches
        const matchesResponse = await axios.get(`${API}/matches`);
        const matchesData = matchesResponse.data;
        setMatches(matchesData);
        
        // Extract unique years from match dates
        const years = [...new Set(matchesData.map(match => 
          new Date(match.date).getFullYear()
        ))].sort((a, b) => b - a); // Sort years in descending order
        
        setAvailableYears(years);
        
        // Set selected year to the most recent year if available
        if (years.length > 0) {
          setSelectedYear(years[0].toString());
        }
        
        // Fetch match types for the form
        if (isAdmin()) {
          const typesResponse = await axios.get(`${API}/match-types`);
          setMatchTypes(typesResponse.data);
          try {
            const leaguesResponse = await axios.get(`${API}/leagues`);
            setLeagues(leaguesResponse.data || []);
          } catch (leagueErr) {
            console.warn("Could not load leagues:", leagueErr);
            setLeagues([]);
          }
        }
        
        setLoading(false);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load data. Please try again.");
        setLoading(false);
      }
    };

    fetchData();
  }, [isAdmin]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewMatch({
      ...newMatch,
      [name]: value
    });
  };

  const handleYearChange = (e) => {
    setSelectedYear(e.target.value);
  };

  const addMatchType = (type) => {
    const instanceName = `${type}${instanceCounter}`;
    setInstanceCounter(instanceCounter + 1);
    
    setNewMatch({
      ...newMatch,
      match_types: [
        ...newMatch.match_types,
        {
          type,
          instance_name: instanceName,
          calibers: []
        }
      ]
    });
  };

  const removeMatchType = (index) => {
    const updatedTypes = [...newMatch.match_types];
    updatedTypes.splice(index, 1);
    
    setNewMatch({
      ...newMatch,
      match_types: updatedTypes
    });
  };

  const toggleCaliber = (matchTypeIndex, caliber) => {
    const updatedTypes = [...newMatch.match_types];
    const currentCaliberIndex = updatedTypes[matchTypeIndex].calibers.indexOf(caliber);
    
    if (currentCaliberIndex === -1) {
      // Add caliber
      updatedTypes[matchTypeIndex].calibers.push(caliber);
    } else {
      // Remove caliber
      updatedTypes[matchTypeIndex].calibers.splice(currentCaliberIndex, 1);
    }
    
    setNewMatch({
      ...newMatch,
      match_types: updatedTypes
    });
  };

  const handleAddMatch = async (e) => {
    e.preventDefault();
    
    if (!newMatch.name || !newMatch.date || !newMatch.location || newMatch.match_types.length === 0) {
      setError("Please fill out all required fields and add at least one match type");
      return;
    }
    
    // Check that each match type has at least one caliber
    for (const matchType of newMatch.match_types) {
      if (matchType.calibers.length === 0) {
        setError(`Please select at least one caliber for ${matchType.instance_name}`);
        return;
      }
    }

    try {
      const response = await axios.post(`${API}/matches`, {
        name: newMatch.name,
        date: new Date(newMatch.date).toISOString(),
        location: newMatch.location,
        match_types: newMatch.match_types,
        aggregate_type: newMatch.aggregate_type,
        // Seed match roster from league when set (backend copies league roster)
        league_id: newMatch.league_id || null,
      });
      
      setMatches([response.data, ...matches]);
      
      // Update available years if a new year is encountered
      const newMatchYear = new Date(response.data.date).getFullYear();
      if (!availableYears.includes(newMatchYear)) {
        setAvailableYears([...availableYears, newMatchYear].sort((a, b) => b - a));
      }
      
      setNewMatch({ 
        name: "", 
        date: new Date().toISOString().split('T')[0],
        location: "",
        match_types: [],
        aggregate_type: "None",
        league_id: ""
      });
    } catch (err) {
      console.error("Error adding match:", err);
      setError("Failed to add match. Please try again.");
    }
  };

  // Filter matches by selected year
  const filteredMatches = selectedYear === "all" 
    ? matches 
    : matches.filter(match => {
        const matchYear = new Date(match.date).getFullYear().toString();
        return matchYear === selectedYear;
      });

  if (loading) return <div className="page text-center">Loading matches...</div>;
  if (error) return <div className="page"><div className="alert-error">{error}</div></div>;

  return (
    <div className="page">
      <div className="flex justify-between items-center mb-6">
        <h1 className="page-title">Matches</h1>

        {/* Year Filter */}
        <div className="flex items-center">
          <label htmlFor="year-filter" className="mr-2 text-ink-700">
            Year:
          </label>
          <select
            id="year-filter"
            value={selectedYear}
            onChange={handleYearChange}
            className="select"
          >
            <option value="all">All Years</option>
            {availableYears.map(year => (
              <option key={year} value={year.toString()}>
                {year}
              </option>
            ))}
          </select>
        </div>
      </div>
      
      {/* Add Match Form - only visible to admins */}
      {isAdmin() && (
        <div className="card card-pad mb-8">
          <h2 className="section-title mb-4">Add New Match</h2>
          <form onSubmit={handleAddMatch} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label htmlFor="name" className="field-label">
                  Match Name
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  value={newMatch.name}
                  onChange={handleInputChange}
                  placeholder="Enter match name"
                  className="input"
                  required
                />
              </div>
              <div>
                <label htmlFor="date" className="field-label">
                  Match Date
                </label>
                <input
                  id="date"
                  name="date"
                  type="date"
                  value={newMatch.date}
                  onChange={handleInputChange}
                  className="input"
                  required
                />
              </div>
              <div>
                <label htmlFor="location" className="field-label">
                  Location
                </label>
                <input
                  id="location"
                  name="location"
                  type="text"
                  value={newMatch.location}
                  onChange={handleInputChange}
                  placeholder="Enter match location"
                  className="input"
                  required
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="aggregate_type" className="field-label">
                  Aggregate Type
                </label>
                <select
                  id="aggregate_type"
                  name="aggregate_type"
                  value={newMatch.aggregate_type}
                  onChange={handleInputChange}
                  className="select"
                >
                  <option value="None">None</option>
                  <option value="1800 (2x900)">1800 (2x900)</option>
                  <option value="1800 (3x600)">1800 (3x600)</option>
                  <option value="2700 (3x900)">2700 (3x900)</option>
                </select>
              </div>
              <div>
                <label htmlFor="league_id" className="field-label">
                  League (optional)
                </label>
                <select
                  id="league_id"
                  name="league_id"
                  value={newMatch.league_id}
                  onChange={handleInputChange}
                  className="select"
                >
                  <option value="">— None (empty match roster) —</option>
                  {leagues.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name}
                      {l.season ? ` (${l.season})` : ""}
                      {` · ${(l.roster_shooter_ids || []).length} shooters`}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-ink-500 mt-1">
                  Seeds this match&apos;s roster from the league. Guests can still be added per match.
                  Manage leagues under <span className="font-medium">Leagues</span>.
                </p>
              </div>
            </div>
            
            <div>
              <label className="field-label mb-2">
                Match Types
              </label>

              <div className="flex flex-wrap gap-2 mb-4">
                {matchTypes && Object.keys(matchTypes).map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => addMatchType(type)}
                    className="btn-secondary btn-sm"
                  >
                    + {type}
                  </button>
                ))}
              </div>
              
              {newMatch.match_types.length > 0 ? (
                <div className="space-y-4">
                  {newMatch.match_types.map((matchType, index) => (
                    <div key={index} className="rounded-xl ring-1 ring-ink-200 p-4">
                      <div className="flex justify-between items-center mb-3">
                        <div className="flex flex-col md:flex-row md:items-center gap-2">
                          <div className="font-medium">{matchType.type}</div>
                          <div className="flex items-center">
                            <span className="text-ink-500 mr-2">Name:</span>
                            <input
                              type="text"
                              value={matchType.instance_name}
                              onChange={(e) => {
                                const updatedTypes = [...newMatch.match_types];
                                updatedTypes[index].instance_name = e.target.value;
                                setNewMatch({
                                  ...newMatch,
                                  match_types: updatedTypes
                                });
                              }}
                              className="input py-1 text-sm"
                              placeholder={`${matchType.type}${instanceCounter}`}
                            />
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeMatchType(index)}
                          className="btn-ghost btn-sm text-rose-600"
                        >
                          Remove
                        </button>
                      </div>

                      <div>
                        <label className="field-label">
                          Calibers
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {[".22", "CF", ".45", "Service Pistol", "Service Revolver", "DR"].map((caliber) => (
                            <button
                              key={caliber}
                              type="button"
                              onClick={() => toggleCaliber(index, caliber)}
                              className={`px-2 py-1 rounded text-sm ${
                                matchType.calibers.includes(caliber)
                                  ? "bg-brand-600 text-white"
                                  : "bg-ink-200 text-ink-700 hover:bg-ink-300"
                              }`}
                            >
                              {caliber}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-ink-500 mb-4">No match types added yet. Click the buttons above to add match types.</div>
              )}
            </div>
            
            <div className="flex justify-end">
              <button
                type="submit"
                className="btn-primary"
              >
                Create Match
              </button>
            </div>
          </form>
        </div>
      )}
      
      {/* Matches List */}
      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th className="text-left">
                Match Name
              </th>
              <th className="text-left">
                Date
              </th>
              <th className="text-left">
                Location
              </th>
              <th className="text-right">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredMatches.length === 0 ? (
              <tr>
                <td colSpan="4" className="text-center text-ink-500">
                  {selectedYear === "all"
                    ? "No matches found"
                    : `No matches found for ${selectedYear}`}
                </td>
              </tr>
            ) : (
              filteredMatches.map((match) => (
                <tr key={match.id}>
                  <td className="whitespace-nowrap">
                    <div className="text-sm font-medium text-ink-900">{match.name}</div>
                    {match.aggregate_type !== "None" && (
                      <div className="text-xs text-brand-600">{match.aggregate_type}</div>
                    )}
                  </td>
                  <td className="whitespace-nowrap">
                    <div className="text-sm text-ink-500">
                      {new Date(match.date).toLocaleDateString()}
                    </div>
                  </td>
                  <td className="whitespace-nowrap">
                    <div className="text-sm text-ink-500">{match.location}</div>
                  </td>
                  <td className="col-actions text-sm font-medium">
                    <div className="flex items-center justify-end gap-4">
                    <Link
                      to={`/matches/${match.id}`}
                      className="text-brand-600 hover:text-brand-800"
                    >
                      View Details
                    </Link>
                    {isAdmin() && (
                      <>
                        <Link
                          to={`/scores/add/${match.id}`}
                          className="text-green-600 hover:text-green-800"
                        >
                          Add Scores
                        </Link>
                        <button
                          onClick={() => {
                            if (window.confirm(`Are you sure you want to delete the match "${match.name}"? This will also delete all scores associated with this match and cannot be undone.`)) {
                              axios.delete(`${API}/matches/${match.id}`)
                                .then(() => {
                                  setMatches(matches.filter(m => m.id !== match.id));
                                  alert(`Match "${match.name}" deleted successfully`);
                                })
                                .catch(err => {
                                  console.error("Error deleting match:", err);
                                  alert("Failed to delete match. Please try again.");
                                });
                            }
                          }}
                          className="text-rose-600 hover:text-rose-800"
                        >
                          Delete
                        </button>
                      </>
                    )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// App Component
function App() {
  return (
    <div className="App min-h-screen bg-ink-50 text-ink-800">
      <AuthProvider>
        <BrowserRouter>
          <Navbar />
          <main className="pt-4 pb-8">
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/unauthorized" element={<Unauthorized />} />
              
              {/* Protected Routes */}
              <Route 
                path="/shooters" 
                element={<ProtectedRoute><ShootersList /></ProtectedRoute>} 
              />
              <Route 
                path="/shooters/:shooterId" 
                element={<ProtectedRoute><ShooterDetail /></ProtectedRoute>} 
              />
              <Route
                path="/leagues"
                element={<ProtectedRoute><LeagueList /></ProtectedRoute>}
              />
              <Route 
                path="/matches" 
                element={<ProtectedRoute><MatchesList /></ProtectedRoute>} 
              />
              <Route
                path="/matches/:matchId"
                element={<ProtectedRoute><MatchReport /></ProtectedRoute>}
              />
              <Route
                path="/labels"
                element={<ScorecardLabels />}
              />
              <Route 
                path="/scores/add/:matchId" 
                element={<ProtectedRoute adminOnly={true}><ScoreEntry /></ProtectedRoute>} 
              />
              <Route 
                path="/scores/edit/:scoreId" 
                element={<ProtectedRoute adminOnly={true}><EditScore /></ProtectedRoute>} 
              />
              <Route 
                path="/matches/:matchId/edit" 
                element={<ProtectedRoute adminOnly={true}><EditMatch /></ProtectedRoute>} 
              />
              <Route 
                path="/admin/users" 
                element={<ProtectedRoute adminOnly={true}><UserManagement /></ProtectedRoute>} 
              />
              <Route 
                path="/change-password" 
                element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} 
              />
            </Routes>
          </main>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;